import React, { useState, useContext } from 'react';
import '../style/body-style.css';
import { useNavigate } from 'react-router-dom';
import LanguageContext from '../components/LanguageContext';
import CK_en from '../resources/CK_en.json';
import CK_fr from '../resources/CK_fr.json';
import {getDate, getBaseUrl} from "../components/Utils";

function SearchResult({ results, totalRecords, pageSize, setPageSize, displayInenigible, setDisplayInenigible }) {
  const { language } = useContext(LanguageContext);
  const CK = language === 'en' ? CK_en : CK_fr;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  document.title = CK['entity_search.search_results.title'];


  const navigate = useNavigate();
  const [loadingEntity, setLoadingEntity] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);


  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleRowClick = async (record) => {
    if(!record.isEligible){
      setDisplayInenigible(true);
      return;
    }
    setLoadingEntity(record.entityIdentifier);
    const requestBody = {
      entityIdentifier: record.entityIdentifier,
      register: record.register,
      entityName: record.entityName,
    };

    const timeoutId = setTimeout(() => {
      alert(
        "There's an issue with your request. Please call ServiceOntario at 416-314-8880 or toll-free at 1-800-361-3223. TTY users can contact us at 416-325-3408 (TTY GTA) or toll-free at 1-800-268-7095 (TTY Canada) for assistance."
      );
      setLoadingEntity(null);
    }, 30000); // 30 seconds

    try {
      const response = await fetch(
        getBaseUrl(process.env.REACT_APP_ENV) + '/getEntity',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      clearTimeout(timeoutId); // Clear the timeout if the request completes

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log('Server response:', data); 

      // Handle error response
      if (data.serverStatusCode) {
        alert(
          data.additionalStatusList[0]?.statusDescription || 'An unknown error occurred.'
        );
        setLoadingEntity(null);
        return; // Exit the function early
      }

      // Validate that entityInfo exists and has entityName
      if (!data.entityInfo || !data.entityInfo.entityName) {
        throw new Error(
          "Unexpected Application Error! Cannot read properties of undefined (reading 'entityName')"
        );
      }

      // Sanitize entityName
      const sanitizedEntityName = encodeURIComponent(data.entityInfo.entityName);

      setTimeout(() => {
        navigate('/SearchDetails', {
          state: {
            record: { ...data.entityInfo, entityName: sanitizedEntityName },
            searchRecord: { ...record},
            hasEmail: data.hasEmail,
            hasPostalCode: data.hasPostalCode,
            hasExistingMailoutRequest: data.hasExistingMailoutRequest,
          },
        });
      }, 2000); // 2 seconds delay

    } catch (error) {
      clearTimeout(timeoutId); // Clear the timeout if an error occurs
      console.error('There was a problem with the fetch operation:', error);
      alert(error.message); // Display the error message to the user
      setLoadingEntity(null);
    }
  };

  totalRecords = results?.entities?.length || 0;

  const LoadingIndicator = () => (
  <div class='ontario-loading-indicator__overlay'>
      <div class='ontario-loading-indicator'>
          <svg class='ontario-loading-indicator__spinner' viewBox="25 25 50 50" xmlns='http://www.w3.org/2000/svg'>
              <circle cx="50" cy="50" r="20" fill="none" stroke-width="4" />
          </svg>
          <p>Loading</p>
      </div>
  </div>
  );

  const totalPages = Math.ceil(totalRecords / pageSize);
  const currentResults = results?.entities?.slice((currentPage - 1) * pageSize, currentPage * pageSize) || [];

  const getRegisterLabel = (register) => {
    switch (register) {
      case 'onbis-corporations':
        return CK['entity_search.search_results.register.onbis-corporations'];
      case 'onbis-partnerships':
        return CK['entity_search.search_results.register.onbis-partnerships'];
      case 'onbis-businessnames':
        return CK['entity_search.search_results.register.onbis-businessnames'];
      case 'onbis-master':
        return CK['entity_search.search_results.register.onbis-master'];
      case 'super_archived':
        return CK['entity_search.search_results.register.super_archived'];
      default:
        return 'No Business type detected';
    }
  };

  const getEntityTypeLabel = (entityType) => {
    switch (entityType) {
      case 'A':
        return CK['get_entity.entity_type.A'];
      case 'B':
        return CK['get_entity.entity_type.B'];
      case 'C':
        return CK['get_entity.entity_type.C'];
      case 'D':
        return CK['get_entity.entity_type.D'];
      case 'F':
        return CK['get_entity.entity_type.F'];
      case 'G':
        return CK['get_entity.entity_type.G'];
      case 'H':
        return CK['get_entity.entity_type.H'];
      case 'J':
        return CK['get_entity.entity_type.J'];
      case 'K':
        return CK['get_entity.entity_type.K'];
      case 'M':
        return CK['get_entity.entity_type.M'];
      case 'N':
        return CK['get_entity.entity_type.N'];
      case 'P':
        return CK['get_entity.entity_type.P'];
      case 'R':
        return CK['get_entity.entity_type.R'];
      case 'S':
        return CK['get_entity.entity_type.S'];
      case 'T':
        return CK['get_entity.entity_type.T'];
      case 'U':
        return CK['get_entity.entity_type.U'];
      case 'W':
        return CK['get_entity.entity_type.W'];
      case 'X':
        return CK['get_entity.entity_type.X'];
      case 'Y':
        return CK['get_entity.entity_type.Y'];
      case 'Z':
        return CK['get_entity.entity_type.Z'];
      case 'ASA':
        return CK['get_entity.entity_type.ASA'];
      case 'CASA':
        return CK['get_entity.entity_type.CASA'];
      case 'COSA':
        return CK['get_entity.entity_type.COSA'];
      case 'GP':
        return CK['get_entity.entity_type.GP'];
      case 'ONLP':
        return CK['get_entity.entity_type.ONLP'];
      case 'EPLP':
        return CK['get_entity.entity_type.EPLP'];
      case 'ONLLP':
        return CK['get_entity.entity_type.ONLLP'];
      case 'EPLLP':
        return CK['get_entity.entity_type.EPLLP'];
      case 'SP':
        return CK['get_entity.entity_type.SP'];
      case 'AN':
        return CK['get_entity.entity_type.AN'];
      case 'PN_GP':
        return CK['get_entity.entity_type.PN_GP'];
      case 'PN_LP':
        return CK['get_entity.entity_type.PN_LP'];
      case 'CO':
        return CK['get_entity.entity_type.CO'];
      case 'AT':
        return CK['get_entity.entity_type.AT'];
      default:
        return 'Unknown entity type';
    }
  };

  const getEntityStatusLabel = (entityStatus) => {
    switch (entityStatus) {
      case 'active':
        return CK['entity_search.search_results.status.active'];
      case 'inactive':
        return CK['entity_search.search_results.status.inactive'];
      case 'referMOF':
        return CK['entity_search.search_results.status.referMOF'];
      case 'referLRO':
        return CK['entity_search.search_results.status.referLRO'];
      case 'referGL':
        return CK['entity_search.search_results.status.referGL'];
      case 'referHJ':
        return CK['entity_search.search_results.status.referHJ'];        
      default:
        return 'Unknown status';
    }
  };

  const handleRetrySearch = () => {
      setDisplayInenigible(false);
  };

  return (
    <div className="ontario-searchResult">
    {displayInenigible && 
        <div className="footer-divider"></div>
    }
    {!displayInenigible && 
        <div className="footer-divider"></div>
    }
    <div className='ontario-row'>
      <div className="ontario-column ">
        {displayInenigible && 
            <div>
                <p>{CK['entity_search.search_results.child_company.explanation']}</p>
                <br/>
                <a tabIndex="0" onClick={handleRetrySearch} onKeyDown={handleRetrySearch} style={{"color":"#0066cc"}}>{CK["entity_search.search_results.explain_page.retry"]}</a>
            </div>
        }

        {!displayInenigible &&
          <span className="search-results__heading ontario-label ontario-label--heading info-label" tabIndex="0">
            {CK["entity_search.search_results.header"]}
          </span>
        }
        {!displayInenigible && totalRecords > 0 &&
          <div tabIndex="0" style={{ paddingBottom: '10px' }}>
            {CK['entity_search.search_results.subheader']}
          </div>
        }
            
        {!displayInenigible && !totalRecords &&
        <div>
          <span tabIndex={0}>
            {CK["entity_search.search_results.no_results"]}
          </span>
        </ div>
          }
        {!displayInenigible && totalRecords < 0 &&
        <div> 
          <span> 
            {CK["entity_search.search_results.no_results"]}
          </span>
        </div>}
        {!displayInenigible && totalRecords > 200 && (
          <div id="max-results" className="ontario-alert" tabIndex="0">
            <span className="ontario-alert__body">The number of search results found exceed the limit of 200 records. Please refine your search.</span>
          </div>
        )}
       {!displayInenigible && totalRecords > 10 && (
            <>
              <div
                className="pagination__banner"
                tabIndex="0"
                style={{ paddingBottom: '10px' }}
              >
                {CK['entity_search.search_results.displaying']}{' '}
                <span id="pageStartNum">{(currentPage - 1) * pageSize + 1}</span> -{' '}
                <span id="pageEndNum">
                  {Math.min(currentPage * pageSize, totalRecords)}{' '}
                </span>
                {CK['entity_search.search_results.of']}{' '}
                <span id="numRecords">{totalRecords} </span>
                {CK['entity_search.search_results.results']}{' '}
              </div>
              <div className="pagination__pages">
                <span
                  id="pagination-item__prev"
                  className="pagination-item"
                  tabIndex="0"
                  onClick={() => setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))}
                  style={{
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    color: currentPage === 1 ? '#666666' : '#0066cc',
                  }}
                >
                  ← {CK["entity_search.search_results.prev"]} </span>
                <span
                  id="pagination-item__1"
                  className="pagination-item"
                  tabIndex="0"
                >
                  {currentPage}
                </span>{' '}
                -{' '}
                <a
                  id="pagination-item__next"
                  className="pagination-item"
                  href="next"
                  tabIndex="0"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
                  }}
                  style={{
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    color: currentPage === totalPages ? '#666666' : '#0066cc',
                  }}
                >
                  {CK["entity_search.search_results.next"]} →
                </a>
              </div>
            </>
          )}
        </div>
        {!displayInenigible && totalRecords > 10 && (
          <div className="ontario-column ontario-column--right">
            <label
              className="ontario-label info-label--right"
              htmlFor="pageSize"
              id="pageSizeLabel"
            >
              {CK['entity_search.search_results.page_size']}
            </label>
            <select
              className="ontario-input ontario-dropdown"
              id="pageSize"
              name="pageSize"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              <option value="10">10</option>
              <option value="20">20</option>
            </select>
          </div>
        )}
      </div>
        
    <div className="ontario-row.search-results__entity-info" >
    <div className="search-result-pan">
      {!displayInenigible && results && currentResults.map((result, index) => (
        <div key={index} className="search-result-item">
        <div className="searchCK">
        <div
          className={`ontario-row search-results__entity-info no-address ${loadingEntity === result.entityIdentifier ? 'loading' : ''}`}
          role="button"
          tabIndex="0"
          onClick={() => handleRowClick(result)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleRowClick(result);
            }
          }}
        >
            <div className="ontario-column search-results__entity-info__col1">
            {getRegisterLabel(result.register)}
            <br />
            <span className="strong">{result.entityName} ({result.entityIdentifier})</span>
            <br />
            <span className="strong">
              {CK["entity_search.search_results.status"]}:
            </span> {getEntityStatusLabel(result.entityStatus)}
            <br />
            {/* Display previouslyKnownAs if it exists */}
            {result.previouslyKnownAs && result.previouslyKnownAs.length > 0 && (
              <span className="strong">
                {CK["entity_search.search_results.previous_name"]}: 
              </span>
            )}
            {result.previouslyKnownAs && result.previouslyKnownAs.map((prevName, prevIndex) => (
              <ul key={prevIndex}>
              <li className="black-bullet">
                {prevName.Name}
              </li>
            </ul>

            ))}
            </div>
            <div className="ontario-column search-results__entity-info__col2">
              <div className="ontario-row">
                <div className="ontario-column strong search-results__entity-info__col21">
                  {CK["entity_search.search_results.registered_date"]}:
                </div>
                <div className="ontario-column search-results__entity-info__col22">
                  {getDate(result.registrationDate, locale)}
                </div>
              </div>
              <div className="ontario-row">
                <div className="ontario-column strong search-results__entity-info__col212 move-upwards">
                  {CK["entity_search.search_results.entity_type"]}:
                </div>
                <div className="ontario-column search-results__entity-info__col22">
                  {getEntityTypeLabel(result.entityType)}
                </div>
              </div>
            </div>
            {loadingEntity === result.entityIdentifier && <LoadingIndicator />}
          </div>
          </div>
        </div>
      ))}
    </div>
    </div>
  </div>
  );
}

export default SearchResult;